import { useEffect, useState, type Dispatch, type FC, type ReactNode, type RefObject, type SetStateAction } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { StepHeader, Layout, NextButton, FlowError, getRadioGroupNameProps } from '@eneco/flows/components';
import { useFormatter } from '@eneco/i18n';
import {
  Box,
  Button,
  Grid,
  RadioGroup,
  RadioTile,
  Stack,
  Stretch,
  Text,
  TextLink,
  VisuallyHidden,
} from '@eneco/sparky';
import { GridProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { Fields } from '@sitecore/types/OfferStep';
import { Controller } from 'react-hook-form';

import { DefaultOfferCard } from './DefaultOfferCard';
import { useOfferStepLogic } from './useOfferStepLogic';
import { OverviewConsumption } from '../../../shared/components';
import { getProductFilterForOffer } from '../../../shared/helpers';
import { filterOffersByType } from '../../../shared/helpers/misc';
import { useBlendAndExtendLogic } from '../../../shared/hooks/useBlendAndExtendLogic';
import { Offer, ContractOption } from '../../../shared/types';
import { PromotionCard } from '../../components/ProductCard/PromotionCard';
import { getOfferPrices } from '../../experiments/exp-n-575/misc/util';

/** SR status after the contract-type filter replaces the offer list. */
const OFFER_FILTER_ANNOUNCEMENT = '{label}: {count, plural, one {# contract getoond} other {# contracten getoond}}.';

/** Accessible name for the offer-card group once the Vast/Variabel/Dynamisch filter already uses the step h1. */
const OFFER_CHOICE_LABEL = 'Kies een contract';
const PROMOTION_DIALOG_LABEL = 'Onze aanbeveling voor jou';
const OFFER_CHOICE_LABEL_ID = 'offerChoice';

interface OfferStepErrorProps {
  textualData?: Fields;
  isNotAuthenticated: boolean;
  isNotAvailable: boolean;
  isNotFound: boolean;
}

const getErrorNotification = ({ textualData, isNotAuthenticated, isNotAvailable, isNotFound }: OfferStepErrorProps) => {
  if (isNotAuthenticated) return textualData?.genericError?.errorNotAuthenticatedNotification;
  if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
  if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;
  return textualData?.genericError?.errorNotification;
};

const getErrorButtonLabel = ({ textualData, isNotAuthenticated, isNotAvailable, isNotFound }: OfferStepErrorProps) => {
  if (isNotAuthenticated) return textualData?.genericError?.loginButtonText;
  if (isNotAvailable || isNotFound) return undefined;
  return textualData?.genericError?.tryAgainButtonText;
};

const OfferStepError: FC<OfferStepErrorProps> = props => (
  <FlowError
    type={props.isNotAuthenticated ? 'AUTHENTIFICATION' : 'GENERIC'}
    alignY="center"
    textualData={{
      notification: getErrorNotification(props),
      buttonLabel: getErrorButtonLabel(props),
    }}
  />
);

interface OfferCardListProps {
  offers: Offer[];
  renderOfferCard: (offer: Offer, index: number) => ReactNode;
  cardRef: RefObject<HTMLDivElement | null>;
  selectedDiscountCodeIndex: number;
  listItemsGap?: GridProps['gap'];
}

const OfferCardList: FC<OfferCardListProps> = ({
  offers,
  renderOfferCard,
  cardRef,
  selectedDiscountCodeIndex,
  listItemsGap,
}) => (
  <Stretch>
    <Grid columns={{ initial: 1, md: 2, lg: 3 }} gap={listItemsGap}>
      {offers.map((offer, index) => (
        <div
          key={offer.type}
          ref={index === selectedDiscountCodeIndex ? cardRef : undefined}
          data-testid="flow-scroll-target"
          data-flow-scroll-target="">
          {renderOfferCard(offer, index)}
        </div>
      ))}
    </Grid>
  </Stretch>
);

interface BaseOfferStepProps {
  /** The hook result from useOfferStepLogic */
  logic: ReturnType<typeof useOfferStepLogic>;

  /** Render function for each offer card */
  renderOfferCard: (offer: Offer, index: number) => ReactNode;

  /** Whether to show the NextButton. Default: true. */
  hasNextButton?: boolean;

  /** Whether to show the moreInfo dialog. Default: true. */
  hasMoreInfoDialog?: boolean;

  /** Where to render OverviewConsumption. 'inline' renders inside Layout.Content, 'footer' renders in Layout.Footer. Default: 'inline'. */
  consumptionLayout?: 'inline' | 'footer';

  /** Gap between products when there are multiple products rendered */
  listItemsGap?: GridProps['gap'];
}

export const BaseOfferStep: FC<BaseOfferStepProps> = ({
  logic,
  renderOfferCard,
  hasNextButton = true,
  hasMoreInfoDialog = true,
  consumptionLayout = 'inline',
  listItemsGap = '6',
}) => {
  const {
    // UI state
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    shouldShowMoreInfoDialog,

    // Form
    handleSubmit,
    control,
    errors,
    isSubmitSuccessful,
    onSuccessfulSubmit,
    selectedDiscountCodeIndex,
    setValue,

    // Offers
    availableOffers,
    showProductToggle,
    offers,
    promotedOffer,

    // Textual data
    textualData,
    flowContext,

    // Energy type
    energyType,

    // Layout
    filteredOptions,
    hasSubtitleLayout,
    gridLayout,
    buttonLayout,

    // Event handlers
    handleValueChange,
    onRadioValueChange,

    // Refs
    cardRef,

    // Flow machine
    sendToFlowMachine,

    // Consumption
    consumptionTextualData,
    consumptionData,
    consumptionEditButton,
  } = logic;

  const { format } = useFormatter();
  const { trackViewComponent } = useTracking();
  const [filterAnnouncement, setFilterAnnouncement] = useState('');
  const { isBlendAndExtend, blendAndExtendStepHeaderTextualData } = useBlendAndExtendLogic();

  const promotionPrices = promotedOffer ? getOfferPrices(promotedOffer) : undefined;

  const [isPromotionDialogOpen, setIsPromotionDialogOpen] = useState(false);
  // Keep promotion-dialog expand state local — sharing `logic.isPriceDetailsOpen` with the
  // offer list cards fights the dialog Expandable and makes the chevron look broken.
  const [isPromotionPriceDetailsOpen, setIsPromotionPriceDetailsOpen] = useState(false);

  useEffect(() => {
    if (promotedOffer && !flowContext.hasDismissedOfferPromotion) {
      // Pre-select the matching contract-type filter so offers are visible behind the dialog.
      handleValueChange(getProductFilterForOffer(promotedOffer));

      setIsPromotionDialogOpen(true);
    }
  }, [promotedOffer, flowContext.hasDismissedOfferPromotion, handleValueChange]);

  useEffect(() => {
    if (!isPromotionDialogOpen) return;

    trackViewComponent({ label: PROMOTION_DIALOG_LABEL, type: 'dialog' });
  }, [isPromotionDialogOpen, trackViewComponent]);

  const dismissOfferPromotion = () => {
    setIsPromotionDialogOpen(false);
    setIsPromotionPriceDetailsOpen(false);

    if (!flowContext.hasDismissedOfferPromotion) {
      sendToFlowMachine({
        type: 'UPDATE_VALUES',
        values: { hasDismissedOfferPromotion: true },
      });
    }
  };

  const handlePromotionDialogOpenChange: Dispatch<SetStateAction<boolean>> = openOrUpdater => {
    const nextOpen = typeof openOrUpdater === 'function' ? openOrUpdater(isPromotionDialogOpen) : openOrUpdater;

    if (nextOpen) {
      setIsPromotionDialogOpen(true);
      return;
    }

    dismissOfferPromotion();
  };

  const handleChoosePromotionOffer = () => {
    if (!promotedOffer?.type) return;

    dismissOfferPromotion();
    setValue('discountCode', promotedOffer.type, { shouldValidate: true, shouldDirty: true });
    void handleSubmit(onSuccessfulSubmit)();
  };

  return (
    <>
      <Layout.Content
        variant="C"
        isLoading={isLoading}
        handleSubmit={handleSubmit}
        onSuccessfulSubmit={onSuccessfulSubmit}>
        {isSuccessful && (
          <>
            <StepHeader textualData={isBlendAndExtend ? blendAndExtendStepHeaderTextualData : textualData} />
            {hasMoreInfoDialog && shouldShowMoreInfoDialog && (
              <TrackedDialog
                title={textualData?.content?.moreInfoDialog?.value?.title}
                trigger={
                  <TextLink emphasis="high">{textualData?.content?.moreInfoDialog?.value?.triggerText}</TextLink>
                }>
                <RichText html={textualData?.content?.moreInfoDialog?.value?.content} />
              </TrackedDialog>
            )}
            {isBlendAndExtend && !shouldShowMoreInfoDialog && textualData?.content?.blendAndExtendInfoDialog && (
              <TrackedDialog
                title={textualData?.content?.blendAndExtendInfoDialog?.value?.title}
                trigger={
                  <TextLink emphasis="high">
                    {textualData?.content?.blendAndExtendInfoDialog?.value?.triggerText}
                  </TextLink>
                }>
                <RichText html={textualData?.content?.blendAndExtendInfoDialog?.value?.content} />
              </TrackedDialog>
            )}
            {showProductToggle && (
              <Box paddingBottom="5">
                {textualData?.content?.contractTypeTitle?.value && (
                  <Box paddingY="3">
                    <Text weight="bold">{textualData?.content.contractTypeTitle.value}</Text>
                  </Box>
                )}
                <VisuallyHidden ariaLive="polite">{filterAnnouncement}</VisuallyHidden>
                <Grid gridTemplateColumns={gridLayout} gap="6">
                  <RadioGroup
                    {...getRadioGroupNameProps()}
                    direction={hasSubtitleLayout || filteredOptions.length > 2 ? 'column' : 'row'}
                    name="Productfilter"
                    value={logic.productFilter}
                    onValueChange={value => {
                      handleValueChange(value);
                      const optionLabel = filteredOptions.find(option => option.name === value)?.label;
                      if (!optionLabel) return;
                      setFilterAnnouncement(
                        format(OFFER_FILTER_ANNOUNCEMENT, {
                          label: optionLabel,
                          count: filterOffersByType(offers, value).length,
                        }),
                      );
                    }}
                    wrap={!hasSubtitleLayout}>
                    <Grid gridTemplateColumns={buttonLayout} gap="3">
                      {filteredOptions?.map((option: ContractOption) => (
                        <RadioTile key={option.name} value={option.name} subtitle={option?.description}>
                          {option.label}
                        </RadioTile>
                      ))}
                    </Grid>
                  </RadioGroup>
                </Grid>
              </Box>
            )}
            {consumptionLayout === 'inline' && (
              <OverviewConsumption
                layout="B"
                textualData={consumptionTextualData}
                data={consumptionData}
                editButton={consumptionEditButton}
              />
            )}

            {promotedOffer && (
              <TrackedDialog
                width="regular"
                isOpen={isPromotionDialogOpen}
                setOpen={handlePromotionDialogOpenChange}
                title={PROMOTION_DIALOG_LABEL}>
                <Stack direction="column" gap="3">
                  {promotedOffer && (
                    <DefaultOfferCard
                      offer={promotedOffer}
                      isOpen={isPromotionPriceDetailsOpen}
                      setOpen={setIsPromotionPriceDetailsOpen}
                      textualData={logic.textualData}
                      flowContext={flowContext}
                      energyType={energyType}
                      CardComponent={PromotionCard}
                      extraCardProps={
                        promotionPrices?.secondary
                          ? { price: promotionPrices.headline, secondaryPrice: promotionPrices.secondary }
                          : undefined
                      }
                    />
                  )}
                  <Stack direction={{ initial: 'column', md: 'row' }} gap="3">
                    <Stretch>
                      <Button type="button" isLoading={isSubmitSuccessful} onClick={handleChoosePromotionOffer}>
                        Kies dit contract
                      </Button>
                    </Stretch>
                    <Stretch>
                      <Button type="button" action="secondary" onClick={dismissOfferPromotion}>
                        Kijk verder
                      </Button>
                    </Stretch>
                  </Stack>
                </Stack>
              </TrackedDialog>
            )}
            {availableOffers.length > 0 ? (
              <>
                {showProductToggle && <VisuallyHidden id={OFFER_CHOICE_LABEL_ID}>{OFFER_CHOICE_LABEL}</VisuallyHidden>}
                <Controller
                  control={control}
                  name="discountCode"
                  render={({ field: { onChange, value, name } }) => (
                    <RadioGroup
                      {...(showProductToggle ? { 'aria-labelledby': OFFER_CHOICE_LABEL_ID } : getRadioGroupNameProps())}
                      name={name}
                      value={value}
                      direction={availableOffers.length >= 2 ? { initial: 'column', md: 'row' } : 'row'}
                      alignY="start"
                      wrap={false}
                      onValueChange={onRadioValueChange ? value => onRadioValueChange(value, onChange) : onChange}
                      error={errors['discountCode']?.message}>
                      <OfferCardList
                        offers={availableOffers}
                        renderOfferCard={renderOfferCard}
                        cardRef={cardRef}
                        selectedDiscountCodeIndex={selectedDiscountCodeIndex}
                        listItemsGap={listItemsGap}
                      />
                    </RadioGroup>
                  )}
                />
                {hasNextButton && (
                  <NextButton isLoading={isSubmitSuccessful} alignX={{ initial: 'justify', md: 'center' }}>
                    {textualData?.content?.nextStepText?.value}
                  </NextButton>
                )}
              </>
            ) : null}
          </>
        )}

        {isFailed && (
          <OfferStepError
            textualData={textualData}
            isNotAuthenticated={isNotAuthenticated}
            isNotAvailable={isNotAvailable}
            isNotFound={isNotFound}
          />
        )}
      </Layout.Content>

      {consumptionLayout === 'footer' && (
        <Layout.Footer isLoading={isLoading || isFailed}>
          <OverviewConsumption
            textualData={consumptionTextualData}
            data={consumptionData}
            editButton={consumptionEditButton}
          />
        </Layout.Footer>
      )}
    </>
  );
};
